/**
 * Bundle Builder - Ryzin Product
 * Handles bundle purchases with dynamic pricing:
 * - 1 roll: Base price ($0.48/pouch)
 * - 2 rolls: Save 10%
 * - 3 rolls: Save 15%
 * - 5+ rolls: Save 20% (Bulk pricing with custom quantity)
 * - Subscribe & Save: real discount from the live Shopify selling plan
 *
 * Features:
 * - Background images with adjustable opacity
 * - Custom background images per flavor via theme settings
 */

(function() {
  'use strict';

  const BundleBuilder = {
    // State management
    state: {
      bundleSize: 1,
      bundleName: 'Single Roll',
      bundleType: 'fixed', // 'fixed' or 'bulk'
      bulkQuantity: 5,
      flavorQuantities: {}, // { variantId: quantity }
      purchaseType: 'onetime',
      quantity: 1,
      sellingPlanId: null, // populated from the live selling plan rendered by Liquid
      prices: {
        oneRoll: 0,       // Will be populated from product data
        twoRolls: 0,      // Calculated dynamically
        threeRolls: 0,    // Calculated dynamically
      },
      discounts: {
        subscription: 0.10, // 10% subscription discount
        twoRoll: 0.10,      // 10% bundle discount
        threeRoll: 0.15,    // 15% bundle discount
        bulkRoll: 0.20,     // 20% bulk discount
      }
    },

    // Initialize
    init: function() {
      this.cacheDom();
      this.loadProductPricing();
      this.loadSellingPlan();
      this.bindEvents();
      this.updatePricing();
      this.initProductGallery();
      this.applyBackgroundImages();
    },

    // Initialize product gallery (Flickity slider and zoom)
    initProductGallery: function() {
      // The gallery uses data-section-type="product" which should be auto-initialized by theme.js
      // No manual initialization needed - theme handles it automatically
    },

    // Load product pricing from DOM and calculate bundle prices
    loadProductPricing: function() {
      // Get base product price from the DOM (rendered by Liquid)
      if (this.el.currentPrice && this.el.currentPrice.dataset.price) {
        const basePrice = parseInt(this.el.currentPrice.dataset.price);

        if (basePrice > 0) {
          // Set single roll price
          this.state.prices.oneRoll = basePrice;

          // Calculate 2-roll bundle with 10% discount
          const twoRollsBeforeDiscount = basePrice * 2;
          this.state.prices.twoRolls = Math.round(twoRollsBeforeDiscount * (1 - this.state.discounts.twoRoll));

          // Calculate 3-roll bundle with 15% discount
          const threeRollsBeforeDiscount = basePrice * 3;
          this.state.prices.threeRolls = Math.round(threeRollsBeforeDiscount * (1 - this.state.discounts.threeRoll));

          console.log('[Bundle] Bundle pricing loaded:', {
            oneRoll: this.formatMoney(this.state.prices.oneRoll),
            twoRolls: this.formatMoney(this.state.prices.twoRolls),
            threeRolls: this.formatMoney(this.state.prices.threeRolls)
          });
        } else {
          console.error('[Bundle] Invalid base price detected:', basePrice);
        }
      } else {
        console.error('[Bundle] Could not load product price from DOM');
      }
    },

    // Load the live selling plan (rendered by Liquid from the real subscription app)
    loadSellingPlan: function() {
      if (!this.el.section) return;
      const planId = this.el.section.dataset.sellingPlanId;
      const subPerRoll = parseInt(this.el.section.dataset.subPricePerRoll, 10);
      if (planId) {
        this.state.sellingPlanId = parseInt(planId, 10);
      }
      // Derive the real subscription discount from the live plan price (e.g. 2876/3595 => 20%)
      if (subPerRoll > 0 && this.state.prices.oneRoll > 0) {
        this.state.discounts.subscription = 1 - (subPerRoll / this.state.prices.oneRoll);
      }
      // Sync state to the toggle's default-selected option. Liquid may render Subscribe as
      // active by default; without this the JS would stay 'onetime' and add a full-price,
      // no-selling_plan line even though Subscribe looks selected.
      const activeOption = document.querySelector('.bundle-builder__purchase-option.active[data-purchase-type]');
      if (activeOption) {
        this.state.purchaseType = activeOption.dataset.purchaseType;
      }
    },

    // Cache DOM elements
    cacheDom: function() {
      this.el = {
        section: document.querySelector('[data-section-type="product-bundle-builder"]'),
        bundleOptions: document.querySelectorAll('[data-bundle-size]'),
        flavorGrid: document.getElementById('flavor-selector'),
        flavorCards: document.querySelectorAll('.bundle-builder__flavor-card'),
        purchaseOptions: document.querySelectorAll('[data-purchase-type]'),
        currentPrice: document.getElementById('bundle-current-price'),
        comparePrice: document.getElementById('bundle-compare-price'),
        savings: document.getElementById('bundle-savings'),
        addToCartBtn: document.getElementById('bundle-add-to-cart'),
        subscriptionBenefits: document.getElementById('subscription-benefits'),
        onetimePrice: document.getElementById('onetime-price'),
        subscriptionPrice: document.getElementById('subscription-price'),
        bulkQuantitySelector: document.getElementById('bulk-quantity-selector'),
        bulkQuantityInput: document.getElementById('bulk-quantity-input'),
        stickyCta: document.getElementById('sticky-cta'),
        stickyCtaBtn: document.getElementById('sticky-cta-btn')
      };
    },

    // Bind event listeners
    bindEvents: function() {
      const self = this;

      // Bundle size selection
      if (this.el.bundleOptions) {
        this.el.bundleOptions.forEach(option => {
          option.addEventListener('click', function() {
            self.selectBundleSize(this);
          });
        });
      }

      // Flavor quantity controls
      if (this.el.flavorCards) {
        this.el.flavorCards.forEach(card => {
          const minusBtn = card.querySelector('[data-action="decrease"]');
          const plusBtn = card.querySelector('[data-action="increase"]');

          if (minusBtn) {
            minusBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              self.adjustFlavorQuantity(card, -1);
            });
          }

          if (plusBtn) {
            plusBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              self.adjustFlavorQuantity(card, 1);
            });
          }
        });
      }

      // Purchase type toggle
      if (this.el.purchaseOptions) {
        this.el.purchaseOptions.forEach(option => {
          option.addEventListener('click', function() {
            self.selectPurchaseType(this);
          });
        });
      }

      // Bulk quantity input
      if (this.el.bulkQuantityInput) {
        this.el.bulkQuantityInput.addEventListener('change', () => {
          self.updateBulkQuantity();
        });
        this.el.bulkQuantityInput.addEventListener('blur', () => {
          self.updateBulkQuantity();
        });
      }

      // Add to cart
      if (this.el.addToCartBtn) {
        this.el.addToCartBtn.addEventListener('click', () => this.addToCart());
      }

      // Sticky CTA scroll detection
      if (this.el.stickyCta && this.el.section) {
        window.addEventListener('scroll', () => this.handleStickyScroll());
      }

      // Sticky CTA click - scroll to top
      if (this.el.stickyCtaBtn && this.el.section) {
        this.el.stickyCtaBtn.addEventListener('click', () => {
          this.el.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    },

    // Apply background images to flavor cards
    applyBackgroundImages: function() {
      this.el.flavorCards.forEach(card => {
        // Check for custom background first
        const customBg = card.dataset.customBg;
        let bgUrl = null;

        if (customBg) {
          // Use custom background from settings
          bgUrl = customBg;
        } else {
          // Fallback to variant image
          const img = card.querySelector('.bundle-builder__flavor-image');
          if (img && img.src) {
            bgUrl = img.src;
          }
        }

        if (bgUrl) {
          card.style.backgroundImage = `url(${bgUrl})`;
          card.style.backgroundSize = 'cover';
          card.style.backgroundPosition = 'center';
          card.style.backgroundRepeat = 'no-repeat';
        }
      });
    },

    // Handle sticky scroll detection
    handleStickyScroll: function() {
      if (!this.el.section || !this.el.stickyCta) return;

      const sectionRect = this.el.section.getBoundingClientRect();
      const sectionBottom = sectionRect.bottom;

      // Show sticky button when section scrolls out of view
      if (sectionBottom < 0) {
        this.el.stickyCta.classList.add('visible');
      } else {
        this.el.stickyCta.classList.remove('visible');
      }
    },

    // Select bundle size
    selectBundleSize: function(element) {
      // Remove active class from all options
      this.el.bundleOptions.forEach(opt => opt.classList.remove('active'));

      // Add active class to selected option
      element.classList.add('active');

      const bundleSize = element.dataset.bundleSize;

      // Check if bulk option
      if (bundleSize === 'bulk') {
        this.state.bundleType = 'bulk';
        this.state.bundleSize = parseInt(this.el.bulkQuantityInput.value) || 5;
        this.state.bundleName = element.dataset.bundleName;

        // Show bulk quantity selector
        if (this.el.bulkQuantitySelector) {
          this.el.bulkQuantitySelector.classList.add('active');
        }
      } else {
        this.state.bundleType = 'fixed';
        this.state.bundleSize = parseInt(bundleSize);
        this.state.bundleName = element.dataset.bundleName;

        // Hide bulk quantity selector
        if (this.el.bulkQuantitySelector) {
          this.el.bulkQuantitySelector.classList.remove('active');
        }
      }

      // Reset flavor quantities
      this.resetFlavorQuantities();

      // Update pricing and flavor requirements
      this.updatePricing();
      this.updateFlavorRequirements();
      this.validateSelection();
    },

    // Update bulk quantity
    updateBulkQuantity: function() {
      if (this.state.bundleType !== 'bulk') return;

      let value = parseInt(this.el.bulkQuantityInput.value);

      // Validate and clamp
      if (value < 5) {
        value = 5;
        this.el.bulkQuantityInput.value = 5;
      } else if (value > 50) {
        value = 50;
        this.el.bulkQuantityInput.value = 50;
      }

      // Update state with clamped value
      this.state.bundleSize = value;
      this.state.bulkQuantity = value;

      // Reset flavor quantities if total exceeds new bundle size
      this.resetFlavorQuantities();

      // Update pricing
      this.updatePricing();
      this.updateFlavorRequirements();
      this.validateSelection();
    },

    // Adjust flavor quantity
    adjustFlavorQuantity: function(card, delta) {
      const variantId = card.dataset.variantId;
      const available = card.dataset.available === 'true';

      if (!available && delta > 0) {
        alert('This flavor is currently out of stock.');
        return;
      }

      // Get current quantity
      const currentQty = this.state.flavorQuantities[variantId] || 0;
      const newQty = Math.max(0, currentQty + delta);

      // Check if we're at bundle limit
      const totalQty = this.getTotalFlavorQuantity();
      const maxQty = this.state.bundleSize;

      if (delta > 0 && totalQty >= maxQty) {
        alert(`You can only select ${maxQty} roll${maxQty > 1 ? 's' : ''} for this bundle.`);
        return;
      }

      // Update quantity
      if (newQty === 0) {
        delete this.state.flavorQuantities[variantId];
      } else {
        this.state.flavorQuantities[variantId] = newQty;
      }

      // Update UI
      this.updateFlavorCardDisplay(card, variantId);
      this.validateSelection();
    },

    // Update flavor card display
    updateFlavorCardDisplay: function(card, variantId) {
      const qty = this.state.flavorQuantities[variantId] || 0;
      const quantityDisplay = card.querySelector('.bundle-builder__quantity-display');
      const minusBtn = card.querySelector('[data-action="decrease"]');
      const plusBtn = card.querySelector('[data-action="increase"]');

      // Update quantity display
      if (quantityDisplay) {
        quantityDisplay.textContent = qty;
      }

      // Update card dataset
      card.dataset.quantity = qty;

      // Disable/enable minus button
      if (minusBtn) {
        minusBtn.disabled = qty === 0;
      }

      // Check if we're at max
      const totalQty = this.getTotalFlavorQuantity();
      const maxQty = this.state.bundleSize;

      // Disable plus button if at max
      if (plusBtn) {
        plusBtn.disabled = totalQty >= maxQty;
      }

      // Update all plus buttons (might affect other cards)
      this.el.flavorCards.forEach(otherCard => {
        const otherPlusBtn = otherCard.querySelector('[data-action="increase"]');
        if (otherPlusBtn) {
          const otherQty = this.state.flavorQuantities[otherCard.dataset.variantId] || 0;
          otherPlusBtn.disabled = totalQty >= maxQty && otherQty === 0;
        }
      });

      // Toggle selected state
      if (qty > 0) {
        card.classList.add('selected');
      } else {
        card.classList.remove('selected');
      }
    },

    // Get total flavor quantity
    getTotalFlavorQuantity: function() {
      return Object.values(this.state.flavorQuantities).reduce((sum, qty) => sum + qty, 0);
    },

    // Reset flavor quantities
    resetFlavorQuantities: function() {
      this.state.flavorQuantities = {};

      // Update all flavor cards
      this.el.flavorCards.forEach(card => {
        this.updateFlavorCardDisplay(card, card.dataset.variantId);
      });
    },

    // Select purchase type
    selectPurchaseType: function(element) {
      // Remove active class from all options
      this.el.purchaseOptions.forEach(opt => opt.classList.remove('active'));

      // Add active class to selected option
      element.classList.add('active');

      // Update state
      this.state.purchaseType = element.dataset.purchaseType;

      // Show/hide subscription benefits
      if (this.el.subscriptionBenefits) {
        if (this.state.purchaseType === 'subscription') {
          this.el.subscriptionBenefits.classList.add('active');
        } else {
          this.el.subscriptionBenefits.classList.remove('active');
        }
      }

      // Update pricing
      this.updatePricing();
    },

    // Update pricing based on selections
    updatePricing: function() {
      let basePrice;
      let compareAtPrice = null;
      let savingsAmount = 0;
      let savingsPercent = 0;

      // Determine base price by bundle size and type
      if (this.state.bundleType === 'bulk') {
        // Bulk pricing: 20% discount
        const bulkTotal = this.state.prices.oneRoll * this.state.bundleSize;
        basePrice = Math.round(bulkTotal * (1 - this.state.discounts.bulkRoll));
        compareAtPrice = bulkTotal;
        savingsAmount = compareAtPrice - basePrice;
        savingsPercent = this.state.discounts.bulkRoll * 100;
      } else {
        // Fixed bundle pricing
        switch(this.state.bundleSize) {
          case 2:
            basePrice = this.state.prices.twoRolls;
            compareAtPrice = this.state.prices.oneRoll * 2;
            savingsAmount = compareAtPrice - basePrice;
            savingsPercent = this.state.discounts.twoRoll * 100;
            break;
          case 3:
            basePrice = this.state.prices.threeRolls;
            compareAtPrice = this.state.prices.oneRoll * 3;
            savingsAmount = compareAtPrice - basePrice;
            savingsPercent = this.state.discounts.threeRoll * 100;
            break;
          default:
            basePrice = this.state.prices.oneRoll;
        }
      }

      // Apply subscription discount — from the live selling plan, on the FULL price.
      // The selling plan discounts each roll's full price at checkout, so we don't
      // stack it on the (display-only) bundle discount; this keeps the shown price == charged price.
      let finalPrice = basePrice;
      if (this.state.purchaseType === 'subscription') {
        const fullTotal = this.state.prices.oneRoll * this.state.bundleSize;
        finalPrice = Math.round(fullTotal * (1 - this.state.discounts.subscription));
        compareAtPrice = fullTotal;

        // Calculate total savings
        savingsAmount = compareAtPrice - finalPrice;
        savingsPercent = Math.round((savingsAmount / compareAtPrice) * 100);
      }

      // Update main price display
      if (this.el.currentPrice) {
        this.el.currentPrice.textContent = this.formatMoney(finalPrice);
        this.el.currentPrice.dataset.price = finalPrice;
      }

      // Update compare at price
      if (this.el.comparePrice && compareAtPrice && compareAtPrice > finalPrice) {
        this.el.comparePrice.textContent = this.formatMoney(compareAtPrice);
        this.el.comparePrice.style.display = '';
      } else if (this.el.comparePrice) {
        this.el.comparePrice.style.display = 'none';
      }

      // Update savings badge
      if (this.el.savings && savingsAmount > 0) {
        this.el.savings.textContent = `Save ${savingsPercent}%`;
        this.el.savings.style.display = '';
      } else if (this.el.savings) {
        this.el.savings.style.display = 'none';
      }

      // Update purchase option prices
      this.updatePurchaseOptionPrices();
      this.updateFlavorRequirements();
    },

    // Update purchase option display prices
    updatePurchaseOptionPrices: function() {
      let basePrice;

      if (this.state.bundleType === 'bulk') {
        const bulkTotal = this.state.prices.oneRoll * this.state.bundleSize;
        basePrice = Math.round(bulkTotal * (1 - this.state.discounts.bulkRoll));
      } else {
        switch(this.state.bundleSize) {
          case 2:
            basePrice = this.state.prices.twoRolls;
            break;
          case 3:
            basePrice = this.state.prices.threeRolls;
            break;
          default:
            basePrice = this.state.prices.oneRoll;
        }
      }

      // Update one-time price
      if (this.el.onetimePrice) {
        this.el.onetimePrice.textContent = this.formatMoney(basePrice);
      }

      // Update subscription price — full price × live plan discount (matches checkout)
      if (this.el.subscriptionPrice) {
        const fullTotal = this.state.prices.oneRoll * this.state.bundleSize;
        const subPrice = Math.round(fullTotal * (1 - this.state.discounts.subscription));
        this.el.subscriptionPrice.textContent = this.formatMoney(subPrice) + '/month';
      }
    },

    // Update flavor requirements message
    updateFlavorRequirements: function() {
      const required = this.state.bundleSize;
      const selected = this.getTotalFlavorQuantity();

      if (this.el.addToCartBtn) {
        const currentPrice = this.el.currentPrice ? this.el.currentPrice.textContent : '';

        if (selected === 0) {
          this.el.addToCartBtn.textContent = `Select ${required} Roll${required > 1 ? 's' : ''} to Continue`;
        } else if (selected < required) {
          this.el.addToCartBtn.textContent = `Select ${required} Rolls (${selected} of ${required} selected)`;
        } else {
          this.el.addToCartBtn.textContent = `Add to Cart - ${currentPrice}`;
        }
      }
    },

    // Validate selection
    validateSelection: function() {
      const totalQty = this.getTotalFlavorQuantity();
      const isValid = totalQty === this.state.bundleSize;

      if (this.el.addToCartBtn) {
        this.el.addToCartBtn.disabled = !isValid;
      }

      this.updateFlavorRequirements();

      console.log('[Bundle] Validation:', {
        totalQty,
        bundleSize: this.state.bundleSize,
        isValid,
        flavorQuantities: this.state.flavorQuantities,
        layout: this.state.layout
      });
    },


    // Add to cart
    addToCart: function() {
      const totalQty = this.getTotalFlavorQuantity();

      if (totalQty !== this.state.bundleSize) {
        alert('Please select the required number of flavors');
        return;
      }

      // Build cart data - add each flavor with its quantity
      const items = [];
      for (const [variantId, quantity] of Object.entries(this.state.flavorQuantities)) {
        if (quantity > 0) {
          const item = {
            id: variantId,
            quantity: quantity,
            properties: {
              // Per-line pack contents only. Quantity = rolls of THIS flavor, so we don't
              // stamp an order-wide roll total on each line (that read as "2 rolls of each").
              'Each roll': '5 cans · 75 pouches'
            }
          };
          // Attach the live selling plan so checkout creates a real (billed) subscription.
          // The plan carries the discount + cadence; one-time orders send no selling_plan.
          if (this.state.purchaseType === 'subscription' && this.state.sellingPlanId) {
            item.selling_plan = this.state.sellingPlanId;
          }
          items.push(item);
        }
      }

      console.log('[Bundle] Adding to cart:', items);

      // Disable button during request
      if (this.el.addToCartBtn) {
        this.el.addToCartBtn.disabled = true;
        this.el.addToCartBtn.textContent = 'Adding to Cart...';
      }

      // Add items to cart
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        console.log('[Bundle] Added to cart:', data);

        // Success - trigger cart drawer or redirect
        if (window.theme && window.theme.cartDrawer) {
          // If theme has cart drawer, trigger refresh
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } else if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.cart) {
          // Trigger theme cart update
          window.location.href = '/cart';
        } else {
          // Default: redirect to cart
          window.location.href = '/cart';
        }
      })
      .catch(error => {
        console.error('[Bundle] Error adding to cart:', error);
        alert('There was an error adding items to your cart. Please try again.');

        // Re-enable button
        if (this.el.addToCartBtn) {
          this.el.addToCartBtn.disabled = false;
          this.updateFlavorRequirements();
        }
      });
    },

    // Format money (cents to dollars)
    formatMoney: function(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      BundleBuilder.init();
    });
  } else {
    BundleBuilder.init();
  }

})();
